/**
 * 文件上传 Hook
 */

import { useCallback, useRef, useState } from 'react';
import { useLatestRef } from './useLatestRef';

/**
 * useFileUpload 配置
 */
export interface UseFileUploadOptions<R = unknown> {
  /**
   * 上传 API 函数
   *
   * @param file - 文件对象
   * @param onProgress - 进度回调
   * @param signal - AbortSignal，可用于取消上传
   */
  uploadApi: (
    file: File,
    onProgress?: (percent: number) => void,
    signal?: AbortSignal,
  ) => Promise<R>;
  /** 允许的文件类型 (如 ['.jpg', '.png', 'image/*']) */
  allowedTypes?: string[];
  /** 最大文件大小 (字节) */
  maxSize?: number;
  /** 上传成功回调 */
  onSuccess?: (result: R, file: File) => void;
  /** 上传失败回调 */
  onError?: (error: Error, file: File) => void;
}

/**
 * useFileUpload 返回值
 */
export interface UseFileUploadReturn<R = unknown> {
  /** 上传中状态 */
  uploading: boolean;
  /** 上传进度 (0-100) */
  progress: number;
  /** 执行上传 */
  upload: (file: File) => Promise<R | undefined>;
  /** 取消上传 */
  abort: () => void;
  /** 重置状态 */
  reset: () => void;
}

/**
 * 文件上传 Hook
 *
 * 管理文件上传的进度、类型验证、大小限制和取消功能。
 *
 * @example
 * ```tsx
 * const { uploading, progress, upload } = useFileUpload({
 *   uploadApi: (file) => uploadFile(file),
 *   allowedTypes: ['.jpg', '.png'],
 *   maxSize: 5 * 1024 * 1024, // 5MB
 *   onSuccess: (result) => message.success('上传成功'),
 *   onError: (err) => message.error(err.message),
 * });
 *
 * <Upload beforeUpload={(file) => { upload(file); return false; }}>
 *   <Button loading={uploading}>上传文件 {uploading && `${progress}%`}</Button>
 * </Upload>
 * ```
 */
export function useFileUpload<R = unknown>(
  options: UseFileUploadOptions<R>,
): UseFileUploadReturn<R> {
  const { uploadApi } = options;

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const abortRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // 用 useLatestRef 稳定回调引用
  const allowedTypesRef = useLatestRef(options.allowedTypes);
  const maxSizeRef = useLatestRef(options.maxSize);
  const onSuccessRef = useLatestRef(options.onSuccess);
  const onErrorRef = useLatestRef(options.onError);

  const upload = useCallback(
    async (file: File) => {
      const allowedTypes = allowedTypesRef.current;
      const maxSize = maxSizeRef.current;

      // 文件类型验证
      if (allowedTypes?.length) {
        const isValid = allowedTypes.some((type) => {
          if (type.startsWith('.')) {
            return file.name.toLowerCase().endsWith(type.toLowerCase());
          }
          if (type.includes('*')) {
            const [mainType] = type.split('/');
            return file.type.startsWith(`${mainType}/`);
          }
          return file.type === type;
        });

        if (!isValid) {
          const error = new Error(
            `Unsupported file type. Allowed: ${allowedTypes.join(', ')}`,
          );
          onErrorRef.current?.(error, file);
          throw error;
        }
      }

      // 文件大小验证
      if (maxSize && file.size > maxSize) {
        const sizeMB = (maxSize / 1024 / 1024).toFixed(1);
        const error = new Error(`File size exceeds limit (${sizeMB}MB)`);
        onErrorRef.current?.(error, file);
        throw error;
      }

      abortRef.current = false;
      // 创建新的 AbortController，供 abort() 方法使用
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setUploading(true);
      setProgress(0);

      try {
        const result = await uploadApi(
          file,
          (percent) => {
            if (!abortRef.current) {
              setProgress(Math.round(percent));
            }
          },
          controller.signal,
        );

        if (!abortRef.current) {
          setProgress(100);
          onSuccessRef.current?.(result, file);
        }

        return result;
      } catch (error) {
        if (!abortRef.current) {
          const err = error instanceof Error ? error : new Error(String(error));
          onErrorRef.current?.(err, file);
          throw err;
        }
        return undefined;
      } finally {
        if (!abortRef.current) {
          setUploading(false);
        }
      }
    },
    [uploadApi, allowedTypesRef, maxSizeRef, onErrorRef, onSuccessRef],
  );

  const abort = useCallback(() => {
    abortRef.current = true;
    // 真正取消网络请求
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setUploading(false);
    setProgress(0);
  }, []);

  const reset = useCallback(() => {
    abortRef.current = false;
    setUploading(false);
    setProgress(0);
  }, []);

  return { uploading, progress, upload, abort, reset };
}
